import { SUPPORT_USER_ID } from '@droidmap/permissions-service-contract'
import type { User } from '@droidmap/user-service-contract'

/**
 * The principal DroidMap support acts as in an assist session
 * (droidmap#2651). It is no user record, so no user map holds it; this
 * stands in for one wherever a row's creator is shown.
 */
const SUPPORT_USER: User = {
  id: SUPPORT_USER_ID,
  userEmail: '',
  username: 'droidmap-support',
  firstName: 'DroidMap',
  lastName: 'Support',
  created: new Date(0),
  createdBy: 'system',
  updated: new Date(0),
  updatedBy: 'system',
}

/**
 * The user to show for a `createdBy` / `updatedBy` id: the record from the
 * map, or DroidMap Support for a change made in an assist session.
 *
 * @example
 * userDisplayName(userFor(userMap, assetSet.createdBy)) // 'DroidMap Support'
 */
export const userFor = (
  userMap: Record<string, User> | undefined,
  userId: string | undefined,
): User | undefined =>
  userId === SUPPORT_USER_ID
    ? SUPPORT_USER
    : userId
      ? userMap?.[userId]
      : undefined

/**
 * Avatar initials for a user record: first letters of first/last name, `?`
 * when neither (or the user) is available.
 */
export const userInitials = (user?: User): string =>
  user ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` || '?' : '?'

/**
 * Display name for a user record: full name, falling back to their email,
 * then to `Unknown` when the record is missing entirely.
 */
export const userDisplayName = (user?: User): string =>
  user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userEmail
    : 'Unknown'
